import { z } from 'zod/v4';
import { variableValueSchema } from './variables';

const savedViewSpecSchema = z.object({
  name: z.string(),
  variable_values: z.record(z.string(), variableValueSchema),
});

export interface SavedView extends z.TypeOf<typeof savedViewSpecSchema> {}

const savedViewSchema = z.object({
  kind: z.literal('SavedView'),
  spec: savedViewSpecSchema,
});

export interface SavedViewDefinition extends z.TypeOf<typeof savedViewSchema> {}

export const savedViewsSchema = z.object({
  kind: z.literal('SavedViews'),
  spec: z.object({
    views: z.array(savedViewSchema),
    default_view_name: z.string().optional(),
  }),
});

export interface SavedViewsDefinition extends z.TypeOf<typeof savedViewsSchema> {}

export const savedViewsToSavedViewsDefinition = (
  savedViews: SavedView[],
  defaultViewName?: string
): SavedViewsDefinition => {
  return {
    kind: 'SavedViews',
    spec: {
      views: savedViews.map((view) => ({
        kind: 'SavedView',
        spec: view,
      })),
      ...(defaultViewName !== undefined && { default_view_name: defaultViewName }),
    },
  };
};

/**
 * Ensures a SavedViewsDefinition exists, returning the existing one or a new empty one.
 */
export function ensureSavedViewsDefinition(existing: SavedViewsDefinition | undefined): SavedViewsDefinition {
  return existing ?? { kind: 'SavedViews', spec: { views: [] } };
}

/** Sets (string), clears (null), or skips (undefined) the default_view_name. */
export function applyDefaultViewName(def: SavedViewsDefinition, name: string | null | undefined): void {
  if (name === undefined) return;
  if (name === null) {
    delete def.spec.default_view_name;
  } else {
    def.spec.default_view_name = name;
  }
}
